import React from "react";
import { useRouteError } from "react-router-dom";
import * as Sentry from "@sentry/react";
import posthog from "posthog-js";

import ErrorFallback from "./ErrorFallback";

/**
 * React Router catches errors thrown while rendering a route and renders this
 * element instead. Without it, Router falls back to its own dev-oriented screen,
 * which both leaks a raw stack trace to users and swallows the error - it never
 * reaches the PostHogErrorBoundary or Sentry's global handler, so the failure is
 * invisible in both tools. Report it here, then show the normal fallback UI.
 */
const RouteErrorBoundary: React.FC = () => {
  const error = useRouteError();

  React.useEffect(() => {
    const err =
      error instanceof Error ? error : new Error(String(error ?? "Unknown"));

    Sentry.captureException(err, { tags: { boundary: "react-router" } });
    posthog.captureException(err, { boundary: "react-router" });
  }, [error]);

  return <ErrorFallback />;
};

export default RouteErrorBoundary;
